"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  label: string;
  value: number;
  suffix: string;
  prefix: string;
  description: string;
  display: string;
};
const stats: Stat[] = [{
  label: "ACTIVE PLOTS",
  value: 85,
  suffix: "+",
  prefix: "",
  display: "85",
  description: "Community-managed garden plots currently flourishing with seasonal vegetables and herbs."
}, {
  label: "COMPOST DIVERTED",
  value: 12,
  suffix: "T",
  prefix: "",
  display: "12",
  description: "Tons of organic kitchen waste transformed into nutrient-rich soil for our neighborhood."
}, {
  label: "WORKSHOPS HELD",
  value: 40,
  suffix: "+",
  prefix: "",
  display: "40",
  description: "Educational sessions on organic farming, seed saving, and sustainable urban gardening."
}, {
  label: "LOCAL HARVEST",
  value: 500,
  suffix: "KG",
  prefix: "",
  display: "500",
  description: "Kilograms of fresh, organic produce shared among our members and local food banks."
}];
function CountUpNumber({
  target,
  display,
  isVisible,
  duration = 1200
}: {
  target: number;
  display: string;
  isVisible: boolean;
  duration?: number;
}) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || startedRef.current) return;
    startedRef.current = true;
    const startTime = performance.now();
    const decimals = display.includes(".") ? display.split(".")[1].length : 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = target * eased;
      setCurrent(value);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setCurrent(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isVisible, target, duration, display]);
  const decimals = display.includes(".") ? display.split(".")[1].length : 0;
  const formatted = decimals > 0 ? current.toFixed(decimals) : Math.floor(current).toString();
  return <span>{formatted}</span>;
}
export default function OurImpactInNumbers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.25
  });
  return <section data-section-id="2503" id="our-impact-in-numbers" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-[200px] px-6">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: `
            radial-gradient(ellipse 800px 400px at 20% 30%, var(--primary) 0%, transparent 40%),
            radial-gradient(ellipse 600px 300px at 80% 70%, var(--primary) 0%, transparent 40%),
            repeating-radial-gradient(circle at 30% 40%, transparent 0px, transparent 38px, var(--primary) 39px, var(--primary) 40px),
            repeating-radial-gradient(circle at 70% 60%, transparent 0px, transparent 46px, var(--primary) 47px, var(--primary) 48px)
          `
    }} />

      <div ref={ref} className="relative max-w-7xl mx-auto">
        {}
        <div className={`flex items-center justify-center gap-4 md:gap-6 mb-10 md:mb-14 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span aria-hidden="true" className="hidden sm:block h-px max-w-[200px] flex-1" style={{
          backgroundColor: "var(--primary)"
        }} />
          <span className="font-default text-xs md:text-sm font-semibold tracking-widest uppercase whitespace-nowrap" style={{
          color: "var(--primary)"
        }}>&nbsp;GROWTH</span>
          <span aria-hidden="true" className="hidden sm:block h-px max-w-[200px] flex-1" style={{
          backgroundColor: "var(--primary)"
        }} />
        </div>

        {}
        <h2 className={`font-default text-center font-bold tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] mb-20 md:mb-28 lg:mb-32 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          OUR GARDEN&apos;S PROGRESS
          <span style={{
          color: "var(--primary)"
        }}>.</span>
        </h2>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-16 gap-x-0 relative">
          {stats.map((stat, i) => <div data-index={i} key={stat.label} className={`relative px-6 md:px-8 text-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: `${200 + i * 120}ms`
        }}>
              {}
              {i > 0 && <span aria-hidden="true" className="hidden lg:block absolute left-0 top-4 bottom-4 w-px" style={{
            backgroundColor: "var(--foreground)",
            opacity: 0.25
          }} />}
              {i === 2 && <span aria-hidden="true" className="hidden sm:block lg:hidden absolute left-0 top-4 bottom-4 w-px" style={{
            backgroundColor: "var(--foreground)",
            opacity: 0.25
          }} />}

              {}
              <p className={`font-default text-[11px] font-semibold uppercase tracking-[0.2em] mb-6 md:text-[8px]`} style={{
            color: "var(--muted-foreground)"
          }}>
                {stat.label}
              </p>

              {}
              <div className={`font-default font-bold leading-none tracking-tighter flex items-start justify-center text-6xl md:text-6xl lg:text-[80px] xl:text-[100px]`}>
                <span className="text-foreground">
                  <CountUpNumber target={stat.value} display={stat.display} isVisible={isVisible} />
                </span>
                <span className="ml-1 md:ml-2" style={{
              color: "var(--primary)"
            }}>
                  {stat.suffix}
                </span>
              </div>

              {}
              <div className="mx-auto mt-8 mb-6 h-[3px] w-8" style={{
            backgroundColor: "var(--primary)"
          }} />

              {}
              <p className={`font-default leading-relaxed max-w-[260px] mx-auto ${i === 0 ? "text-base md:text-sm" : "text-base"}`} style={{
            color: "var(--muted-foreground)"
          }}>
                {stat.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}